import { Box, Stack, Typography } from '@mui/material';
import type { Condition } from '@repo/api-gw-sdk';

import ConditionViewer from '@/components/conditions/ConditionViewer';
import {
  sourceAccount,
  apps,
  cloudProvider,
  dataClasses,
  resourceGroupName,
  resourceId,
  resourceName,
  resourceType,
  sourceRegion,
  subnets,
  tags,
  vpc,
  environment,
} from '@/components/queryBuilder/properties';
import type { FilterProperty } from '@/types/advanceFilter';

import {
  DynamicResourcesAllExcluded,
  DynamicResourcesAllIncluded,
} from '../../app/(dashboard)/settings/backup-policies/backup-scopes/[tab]/dynamicResourcesEmptyState';

export const backupScopeSupportedProperties: FilterProperty[][] = [
  [resourceType, cloudProvider, dataClasses, environment, apps],
  [sourceAccount, sourceRegion, vpc, subnets, resourceGroupName],
  [resourceName, resourceId, tags],
];

export type ResourcesScopeType =
  | 'allIncluded'
  | 'allExcluded'
  | 'useConditions';

const isEmptyCondition = (condition: Condition) => {
  return (
    !condition.property &&
    !condition.operator &&
    !condition.conditions?.length &&
    condition.value?.length === 1
  );
};

export const getResourcesScopeType = (
  condition: Condition
): ResourcesScopeType => {
  if (isEmptyCondition(condition)) {
    if (condition.value?.[0] === 'true') {
      return 'allIncluded';
    }
    if (condition.value?.[0] === 'false') {
      return 'allExcluded';
    }
  }
  return 'useConditions';
};

interface ResourceSelectionDynamicViewerProps {
  condition?: Condition;
  conditionTitle: string;
  resourcesScopeType: ResourcesScopeType;
  allExcludedDescription: string;
  allIncludedDescription: string;
}

export const ResourceSelectionDynamicViewer = ({
  condition,
  conditionTitle,
  resourcesScopeType,
  allExcludedDescription,
  allIncludedDescription,
}: ResourceSelectionDynamicViewerProps) => {
  return (
    <Box width='100%'>
      {resourcesScopeType === 'useConditions' && condition ? (
        <Stack gap='24px'>
          <Typography variant='body1'>{conditionTitle}</Typography>
          <ConditionViewer
            condition={condition}
            supportProperties={backupScopeSupportedProperties.flat()}
          />
        </Stack>
      ) : resourcesScopeType === 'allExcluded' ? (
        <DynamicResourcesAllExcluded
          description={allExcludedDescription}
          imageSize='small'
        />
      ) : (
        <DynamicResourcesAllIncluded
          description={allIncludedDescription}
          imageSize='small'
        />
      )}
    </Box>
  );
};
